import { useEffect, useState } from "react";
import api from "@/utils/api";
import { Activity, TrendingUp, TrendingDown, Scale, Ruler, History, Calendar, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const MemberHealth = () => {
  const { formatDate } = useSettings();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [member, setMember] = useState(null);
  
  // Form State
  const [editingLogId, setEditingLogId] = useState(null);
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchHealthData();
  }, []);

  const fetchHealthData = async () => {
    try {
      const [logsRes, memberRes] = await Promise.all([
        api.get('/members/me/health-logs'),
        api.get('/members/me')
      ]);
      
      setLogs(logsRes.data);
      // Handle different response structures: {member: ...}, {user: ...}, or directly ...
      const memberData = memberRes.data.member || memberRes.data.user || memberRes.data;
      setMember(memberData);
      
      // Pre-fill height if available
      if (memberData?.height && !height) {
        setHeight(memberData.height);
      }
      
      // Pre-fill weight from last log or member profile if not already set
      if (!weight) {
        if (logsRes.data.length > 0) {
          setWeight(logsRes.data[0].weight);
        } else if (memberData?.weight) {
          setWeight(memberData.weight);
        }
      }
    } catch (error) {
      console.error("Failed to load health data:", error);
      const errorMessage = error.response?.data?.detail || "Failed to load health data";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (log) => {
    setEditingLogId(log.id);
    setDate(log.date.split('T')[0]);
    setWeight(log.weight);
    setHeight(log.height || "");
    setNotes(log.notes || "");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingLogId(null);
    setDate(new Date().toISOString().split('T')[0]);
    setNotes("");
    // Keep current weight/height or reset to latest? 
    // Let's just keep whatever is in the form or reset to latest log
    if (logs.length > 0) {
      setWeight(logs[0].weight);
      setHeight(logs[0].height || "");
    }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm("Are you sure you want to delete this health log?")) return;

    try {
      await api.delete(`/members/me/health-logs/${logId}`);
      toast.success("Health log deleted successfully");
      fetchHealthData();
    } catch (error) {
      console.error("Failed to delete health log:", error);
      toast.error(error.response?.data?.detail || "Failed to delete health log");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!weight) {
      toast.error("Please enter your weight");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        date: date,
        weight: parseFloat(weight),
        height: height ? parseFloat(height) : null,
        notes: notes
      };

      if (editingLogId) {
        await api.patch(`/members/me/health-logs/${editingLogId}`, payload);
        toast.success("Health log updated successfully");
        setEditingLogId(null);
      } else {
        await api.post('/members/me/health-logs', payload);
        toast.success("Health log added successfully");
      }
      
      setNotes(""); // Clear notes
      if (editingLogId) {
          // If we finished editing, reset date to today
          setDate(new Date().toISOString().split('T')[0]);
      }
      fetchHealthData(); // Refresh data
    } catch (error) {
      console.error("Failed to save health log:", error);
      const errorMessage = error.response?.data?.detail || "Failed to save health log";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate Stats
  const currentWeight = logs.length > 0 ? logs[0].weight : (member?.weight || 0);
  const currentHeight = logs.length > 0 && logs[0].height ? logs[0].height : (member?.height || 0);
  
  // BMI = weight (kg) / (height (m))^2
  // Assuming height is in cm
  const bmi = currentWeight && currentHeight 
    ? (currentWeight / Math.pow(currentHeight / 100, 2)).toFixed(1) 
    : "N/A";

  const getBMIStatus = (bmiValue) => {
    if (bmiValue === "N/A") return { label: "Unknown", color: "text-zinc-500" };
    const val = parseFloat(bmiValue);
    if (val < 18.5) return { label: "Underweight", color: "text-blue-500" };
    if (val < 25) return { label: "Healthy Weight", color: "text-green-500" };
    if (val < 30) return { label: "Overweight", color: "text-yellow-500" };
    return { label: "Obese", color: "text-red-500" };
  };

  const bmiStatus = getBMIStatus(bmi);

  // Weight Trend
  const chartData = [...logs].reverse().map(log => ({
    date: new Date(log.date).toLocaleDateString(),
    weight: log.weight
  }));

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">My Health</h1>
          <p className="text-zinc-500">Track your fitness journey and body metrics</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Stats Cards */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current Weight</CardTitle>
            <Scale className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currentWeight} kg</div>
            <p className="text-xs text-muted-foreground">
              Last updated: {logs.length > 0 ? formatDate(logs[0].date) : "Never"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">BMI Score</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{bmi}</div>
            <p className={`text-xs font-medium ${bmiStatus.color}`}>
              {bmiStatus.label}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Logs Count</CardTitle>
            <History className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{logs.length}</div>
            <p className="text-xs text-muted-foreground">
              Total entries recorded
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Log Entry Form */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>{editingLogId ? "Edit Health Log" : "Log Today's Stats"}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3 h-4 w-4 text-zinc-400" />
                  <Input
                    id="date"
                    type="date"
                    max={new Date().toISOString().split('T')[0]}
                    className="pl-9"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="weight">Weight (kg)</Label>
                <div className="relative">
                  <Scale className="absolute left-3 top-3 h-4 w-4 text-zinc-400" />
                  <Input
                    id="weight"
                    type="number"
                    step="0.1"
                    placeholder="0.0"
                    className="pl-9"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="height">Height (cm)</Label>
                <div className="relative">
                  <Ruler className="absolute left-3 top-3 h-4 w-4 text-zinc-400" />
                  <Input
                    id="height"
                    type="number"
                    step="0.1"
                    placeholder="0.0"
                    className="pl-9"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="How are you feeling today?"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1 bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold" disabled={submitting}>
                  {submitting ? "Saving..." : (editingLogId ? "Update Log" : "Save Log")}
                </Button>
                {editingLogId && (
                  <Button type="button" variant="outline" onClick={handleCancelEdit} disabled={submitting}>
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Weight Trend</CardTitle>
          </CardHeader>
          <CardContent className="h-[300px]">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#888888" fontSize={12} tickLine={false} axisLine={false} domain={['auto', 'auto']} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    itemStyle={{ color: '#18181b' }}
                  />
                  <Line type="monotone" dataKey="weight" stroke="#84cc16" strokeWidth={2} activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-zinc-400">
                No data available yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* History List - Mobile Cards */}
      <div className="md:hidden space-y-4">
        <h3 className="font-bold text-lg text-zinc-900 px-1">History</h3>
        {logs.length > 0 ? (
          logs.map((log) => (
            <div key={log.id} className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-medium text-zinc-900">{formatDate(log.date)}</div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(log)}>
                    <Pencil className="h-4 w-4 text-zinc-500 hover:text-blue-500" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(log.id)}>
                    <Trash2 className="h-4 w-4 text-zinc-500 hover:text-red-500" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm mb-2">
                <div>
                  <span className="text-zinc-500 block text-xs">Weight</span>
                  <span className="font-medium">{log.weight} kg</span>
                </div>
                <div>
                  <span className="text-zinc-500 block text-xs">Height</span>
                  <span className="font-medium">{log.height ? `${log.height} cm` : '-'}</span>
                </div>
              </div>
              {log.notes && (
                <div className="text-zinc-500 text-sm bg-zinc-50 p-2 rounded mt-2">
                  {log.notes}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center p-8 text-zinc-500 bg-white border border-zinc-200 rounded-xl">
            No logs found. Start tracking your health today!
          </div>
        )}
      </div>

      {/* History Table - Desktop */}
      <Card className="hidden md:block">
        <CardHeader>
          <CardTitle>History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative w-full overflow-auto">
            <table className="w-full caption-bottom text-sm">
              <thead className="[&_tr]:border-b">
                <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Date</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Weight</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Height</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Notes</th>
                  <th className="h-12 px-4 text-right align-middle font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {logs.length > 0 ? (
                  logs.map((log) => (
                    <tr key={log.id} className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                      <td className="p-4 align-middle">{formatDate(log.date)}</td>
                      <td className="p-4 align-middle font-medium">{log.weight} kg</td>
                      <td className="p-4 align-middle">{log.height ? `${log.height} cm` : '-'}</td>
                      <td className="p-4 align-middle text-zinc-500 max-w-[300px] truncate">{log.notes || '-'}</td>
                      <td className="p-4 align-middle text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(log)}>
                            <Pencil className="h-4 w-4 text-zinc-500 hover:text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(log.id)}>
                            <Trash2 className="h-4 w-4 text-zinc-500 hover:text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="p-4 text-center text-zinc-500">
                      No logs found. Start tracking your health today!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MemberHealth;
